import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acceptInvitation, resetPassword } from '../api/auth';
import { ApiClientError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';
import { PreferenceToggles } from '../lib/PreferencesProvider';

export type SetPasswordMode = 'reset' | 'invitation';

/**
 * FEAT-001 — setting a password from an emailed link.
 *
 * **One component, two routes.** Resetting a forgotten password and accepting an
 * invitation are the same form: a token from the query string, one password,
 * submit, 204, go and sign in. Only the wording and the endpoint differ, and two
 * near-identical files would drift apart the first time one of them was fixed.
 *
 * Both links were dead until this existed. `/reset-password?token=…` matched no
 * route, so `path="*"` — which sits *inside* `RequireSession` — sent an
 * unauthenticated visitor to the sign-in screen and discarded the token on the
 * way. Every reset email the platform has ever sent led nowhere.
 */
export function SetPasswordPage({ mode }: { mode: SetPasswordMode }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [params] = useSearchParams();

  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const prefix = mode === 'invitation' ? 'invitation' : 'reset';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldError(null);

    /*
     * Compared in the browser only, and never sent. This is not validation — the
     * server has no idea a second field exists. It is here because a typo in a
     * field you cannot read, on an account you are already locked out of, is a
     * genuinely bad afternoon.
     */
    if (password !== confirmation) {
      setFieldError(t('passwords_do_not_match'));
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'invitation') await acceptInvitation(token, password);
      else await resetPassword(token, password);

      /*
       * The server has just revoked every session for this user, including this
       * browser's. Without clearing local state the shell would still think it
       * was signed in and every request would 401.
       */
      await signOut();

      navigate('/login', { replace: true, state: { passwordSet: true } });
    } catch (caught) {
      if (!(caught instanceof ApiClientError)) {
        setError(t('login_network_error'));
        return;
      }

      // 410 — used already, or past its expiry. There is no way to have known
      // before spending it, which is deliberate: see `api/auth.ts`.
      if (caught.code === 'token_consumed') setIsExpired(true);
      else if (caught.fieldErrors.password) setFieldError(caught.fieldErrors.password);
      else setError(caught.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  /*
   * A missing token is knowable without asking the server, so it is answered
   * immediately rather than after a wasted submission.
   */
  if (!token || isExpired) {
    return (
      <Shell>
        <div className="login-sent" role="status">
          <h1 className="login-title">{t('link_invalid_title')}</h1>
          <p className="login-lead">{t(`link_invalid_${prefix}`)}</p>

          {mode === 'reset' ? (
            <Link className="btn primary login-submit" to="/forgot-password">
              {t('request_new_link')}
            </Link>
          ) : null}

          <Link className="login-alt" to="/login">
            {t('back_to_sign_in')}
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={handleSubmit} noValidate>
        <h1 className="login-title">{t(`${prefix}_title`)}</h1>
        <p className="login-lead">{t(`${prefix}_intro`)}</p>

        {error ? (
          <div className="login-error" role="alert">
            <Icon name="info" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="password">{t('new_password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            aria-describedby="password-hint"
          />
          {/*
            A hint, not a rule. `PasswordService.validate` on the server is
            authoritative, and reimplementing it here would give two answers that
            eventually disagree — so a rejected password renders the server's own
            message rather than anything decided in the browser.
          */}
          <span className="hint" id="password-hint">
            {t('password_min_hint')}
          </span>
        </div>

        <div className="field">
          <label htmlFor="confirmation">{t('confirm_password')}</label>
          <input
            id="confirmation"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            disabled={isSubmitting}
          />
          {fieldError ? (
            <span className="field-error" role="alert">
              {fieldError}
            </span>
          ) : null}
        </div>

        <button type="submit" className="btn primary login-submit" disabled={isSubmitting}>
          {isSubmitting ? t('saving') : t('set_password')}
        </button>
      </form>
    </Shell>
  );
}

/** The card the prototype already draws for signing in. */
function Shell({ children }: { children: ReactElement }): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="login-shell">
      <div className="card login-card">
        <div className="login-brand">
          <img src="/logo.png" alt="Boud" />
          <div className="sub">{t('boud_sub')}</div>
        </div>

        {children}

        <div className="login-toggles">
          <PreferenceToggles />
        </div>
      </div>
    </div>
  );
}
